import { createInertiaApp } from '@inertiajs/react'
import { createElement, Fragment } from 'react'
import type { ComponentType } from 'react'
import type { ReactNodeCompat } from '@/types/react-compat'
import { logError, logWarn } from '@/lib/logger'
import posthog from 'posthog-js'
import { PosthogTracker } from '@/components/analytics/PosthogTracker'
import { PublicMotionBootTracker } from '@/components/public/PublicMotionProvider'
import { renderInertiaRoot } from './renderInertiaRoot'

// Temporary type definition, until @inertiajs/react provides one
type ResolvedComponent = {
  default: ComponentType & { layout?: (page: ReactNodeCompat) => ReactNodeCompat }
}

const posthogDefaultsVersion = '2026-01-30' as unknown as NonNullable<
  Parameters<typeof posthog.init>[1]
>['defaults']

createInertiaApp({
  // Set default page title
  // see https://inertia-rails.dev/guide/title-and-meta
  //
  // title: title => title ? `${title} - App` : 'App',

  // Disable progress bar
  //
  // see https://inertia-rails.dev/guide/progress-indicators
  // progress: false,

  resolve: (name) => {
    const pages = import.meta.glob<ResolvedComponent>(
      ['../pages/**/*.tsx', '!../pages/**/*.test.tsx', '!../pages/**/__tests__/**'],
      {
        eager: true,
      },
    )
    const page = pages[`../pages/${name}.tsx`]
    if (!page) {
      logError(`Missing Inertia page component: '${name}.tsx'`)
    }

    const pageComponent = page?.default
    if (pageComponent) {
      const existingLayout = pageComponent.layout
      pageComponent.layout = (pageNode: ReactNodeCompat) =>
        createElement(
          Fragment,
          null,
          createElement(PosthogTracker),
          existingLayout ? existingLayout(pageNode) : pageNode,
        )
    }

    return page
  },

  setup({ el, App, props }) {
    if (el) {
      const posthogKey = import.meta.env.VITE_POSTHOG_API_KEY?.trim()
      const posthogHost = 'https://h.emovia.com'
      const posthogEnabled = Boolean(posthogKey)

      if (posthogEnabled) {
        posthog.init(posthogKey, {
          api_host: posthogHost,
          defaults: posthogDefaultsVersion,
          capture_pageview: 'history_change',
          autocapture: {
            element_allowlist: ['a', 'button', 'form'],
            dom_event_allowlist: ['click', 'submit'],
          },
        })
      } else {
        logWarn('PostHog disabilitato: manca VITE_POSTHOG_API_KEY')
      }

      renderInertiaRoot(el, createElement(PublicMotionBootTracker, null, createElement(App, props)))
    } else {
      logError(
        'Missing root element.\n\n' +
          'If you see this error, it probably means you load Inertia.js on non-Inertia pages.\n' +
          'Consider moving <%= vite_typescript_tag "inertia" %> to the Inertia-specific layout instead.',
      )
    }
  },
})
